// @ts-ignore
import React from "react";

import { DEFAULT_VALUES, useLocalizedHref } from "@libraries/web-framework";
import { FormattedMessage, useIntl } from "react-intl";

import { ImageProps } from "@atlassiansox/wpl-image";

import { ButtonColors } from "@/components/wpl/button/src";
import {
    DataEventProps,
    ExportableAdgColorTypes,
    LogoBrand,
    ProductKeyTypes
} from "@/libraries/wpl/foundations/src/index";
import ImageChecklistSide from "@/patterns/image-checklist-side";

import { PaidLandingTemplate } from "../../templates/paid-landing/src";

export const JiraSoftwarePagePaidLpJswTemplate = () => {
    const intl = useIntl();

    const props = {
        productNavProps: {
            productHref: useLocalizedHref("/software/jira"),
            productName: "jirasoftware" as LogoBrand,
            ariaLabel: intl.formatMessage({
                id: "wacJiraSoftwareNavigation.logo-label",
                defaultMessage: "Jira Software logo"
            }),
            dataProductKey: "jiraSoftware" as ProductKeyTypes,
            showFreeHrefButton: false
        },
        bannerAlertProps: {
            identifier:
                "jira-software-page.banner-alert.atlassian-intelligence",
            text: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.banner-alert.text",
                defaultMessage:
                    "Get early access to Atlassian Intelligence, your new virtual teammate that deeply understands how teams collaborate to accelerate work."
            }),
            linkText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.banner-alert.link-text",
                defaultMessage: "Learn more"
            }),
            linkUrl: useLocalizedHref("/software/artificial-intelligence")
        },
        heroProps: {
            styles: {
                "backgroundColor": "#DEEBFF",
                "backgroundImage": `var(--hero-bg-1, linear-gradient(transparent, transparent)), linear-gradient(287.15deg, #FAFBFC 0%, #DEEBFF 8.06%, #B3D4FF 35.45%, #DEEBFF 77.6%, #FAFBFC 100%)`,
                "backgroundPosition": "top right, 0 0",
                "backgroundRepeat": "no-repeat",
                "backgroundSize": "auto 100% , auto",

                "@media only screen and (min-width: 1024px)": {
                    "--hero-bg-1": `url("https://wac-cdn.atlassian.com/dam/jcr:f96e5a0a-7af5-4cc6-884c-b51416e65e41/JSM_BG%20Graphics.png?cdnVersion=1276")`
                }
            },
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.hero.header",
                defaultMessage: "There’s a Jira template for that"
            }),
            secondaryText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.hero.body",
                defaultMessage:
                    "Start your project off right with a pre-configured template built for all teams"
            }),
            featuresLabel: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.hero.listHeading",
                defaultMessage: "Included in your free plan:"
            }),
            featuresList: [
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswTemplate.hero.listItem1",
                    defaultMessage: "10 user licenses"
                }),
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswTemplate.hero.listItem2",
                    defaultMessage: "Workflows and automation"
                }),
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswTemplate.hero.listItem3",
                    defaultMessage:
                        "Project templates — including scrum, kanban, and more"
                }),
                intl.formatMessage({
                    id: "JiraSoftwarePagePaidLpJswTemplate.hero.listItem4",
                    defaultMessage: "Integrations with thousands of other tools"
                })
            ]
        },
        tabsProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.tabs.heading",
                defaultMessage:
                    "Jira Software comes with a variety of project templates to help teams get set up quickly"
            }),
            tabStyles: {
                defaultBG: "n0" as ExportableAdgColorTypes,
                defaultText: "n900" as ExportableAdgColorTypes,
                hoverBG: "n0" as ExportableAdgColorTypes,
                hoverText: "n900" as ExportableAdgColorTypes,
                activeBG: "b50" as ExportableAdgColorTypes,
                activeText: "n900" as ExportableAdgColorTypes
            },
            styles: {
                "--lp-tabs-hover": "#deebff",
                "--lp-tabs-unselected": "#6b778c"
            },
            tabs: [
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab1.textLabel",
                        defaultMessage: "Scrum Template"
                    }),
                    id: "scrum",
                    component: Scrum()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab2.textLabel",
                        defaultMessage: "Kanban Template"
                    }),
                    id: "kanban",
                    component: Kanban()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab3.textLabel",
                        defaultMessage: "Bug Tracker Template"
                    }),
                    id: "bug-tracker",
                    component: BugTracker()
                },
                {
                    textLabel: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab4.textLabel",
                        defaultMessage: "DevOps Template"
                    }),
                    id: "devops",
                    component: DevOps()
                }
            ]
        },
        ctaProps: {
            styles: {
                "color": "#ffffff",
                "backgroundImage":
                    "url(https://wac-cdn.atlassian.com/dam/jcr:efb3c307-d2fb-42af-8536-45461774796a/JSW_Blue%20Grid.png)",
                "backgroundPosition": "center",
                "backgroundSize": "cover",
                "backgroundRepeat": "no-repeat",
                "@media only screen and (min-width: 768px)": {
                    backgroundImage:
                        "url(https://wac-cdn.atlassian.com/dam/jcr:8a9ac4bd-71f9-4af4-b859-f9c132e12f80/JSW_CTA_bg.png)"
                }
            },
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.ctaSection.header",
                defaultMessage:
                    "Move fast, stay aligned, and build better - together"
            }),
            ctaProps: {
                buttonProps: {
                    color: "yellow" as ButtonColors,
                    url: "#start-trial",
                    text: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.ctaSection.button",
                        defaultMessage: "Get it free"
                    }),
                    dataEventProps: {
                        "data-event": "clicked",
                        "data-event-container": "ctaSection",
                        "data-event-component": "linkButton",
                        "data-product-key": "jiraSoftware"
                    } as DataEventProps
                }
            },
            backgroundProperties: {
                /* this is deprecated, but need it temporarily to override current behavior */
                textColor: "n0" as ExportableAdgColorTypes
            }
        },
        testimonialProps: {
            headerText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.heading",
                defaultMessage: "Trusted by over 100,000 customers world-wide"
            }),
            logos: [
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:4cba45db-e328-4abd-88ea-bfe276355cb5/Square%20Logo.svg",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.squareAlt",
                        defaultMessage: "Square logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:db51d228-2145-498b-ab73-064aa651770d/ebay%20logo.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.ebayAlt",
                        defaultMessage: "eBay logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:7db3e103-186c-4413-950d-dea2f2a5755c/Spotify%20logo.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.spotifyAlt",
                        defaultMessage: "Spotify logo"
                    }),
                    loading: "lazy"
                },
                {
                    src: "https://wac-cdn.atlassian.com/dam/jcr:4d6ede5b-a9ea-410b-baab-6cb7166c080d/Cisco%20Logo.svg?cdnVersion=ecc",
                    alt: intl.formatMessage({
                        id: "JiraSoftwarePagePaidLpJswTemplate.socialProof.ciscoAlt",
                        defaultMessage: "Cisco logo"
                    }),
                    loading: "lazy"
                }
            ] as ImageProps[]
        },
        alternatingTextAndImageProps: {
            backgroundImage:
                "linear-gradient(225deg, #deebff 0%, #ffffff 100%)",
            horizontalRuleColor: "#b2d4ff",
            imageWidth: "100%",
            imageTextSideRowOnePaddingBelowLargeScreen: "32px 0 16px",
            imageTextSideRowTwoPadding: "16px 0",
            imageTextSideRowThreePaddingBelowLargeScreen: "16px 0 32px",
            //Row 1
            rowOneHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowOneHeader",
                defaultMessage: "Customize how your team’s work flows"
            }),
            rowOneBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowOneBody",
                defaultMessage:
                    "Set up, clean up, and automate even the most complicated project workflows."
            }),
            rowOneImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:55e7df45-2c5c-4cd7-b281-c3c6934bbb77/maximize-efficiency%201.png?cdnVersion=ece",
            rowOneImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowOneImageAltText",
                defaultMessage: " "
            }),
            //Row 2
            rowTwoHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.secondTextHeader",
                defaultMessage: "Choose your own adventure"
            }),
            rowTwoBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.secondTextBody",
                defaultMessage:
                    "Jira Software is flexible enough to mold to your team’s own unique way of working, whether it’s Scrum, Kanban, or something in between."
            }),
            rowTwoImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:11c76bab-4f34-4d44-bbb7-9b6dcbc9ed73/choose_your_own_adventure%201.png?cdnVersion=ece",
            rowTwoImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowTwoImageAltText",
                defaultMessage: " "
            }),
            //Row 3
            rowThreeHeader: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowThreeHeader",
                defaultMessage: "Reports and insights"
            }),
            rowThreeBody: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowThreeBody",
                defaultMessage:
                    "Out-of-the-box reports and dashboards in Jira Software offer critical insights within the context of your work to ensure your teams are always up to date and set up for success."
            }),
            rowThreeImageLink:
                "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:205d91ea-00c3-4105-b719-23d3c1de3b3a/reports_and_insights%201.png?cdnVersion=ece",
            rowThreeImageAltText: intl.formatMessage({
                id: "JiraSoftwarePagePaidLpJswTemplate.alternatingTextAndImage.rowThreeImageAltText",
                defaultMessage: " "
            })
        }
    };
    return <PaidLandingTemplate {...props} />;
};

/* Tabs */

const Scrum = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab1.heading",
        defaultMessage: "Easily plan, track and manage work across sprints."
    });
    const listItems = [
        <FormattedMessage
            key="tabs.tab1.item1"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab1.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Plan upcoming work in a backlog:</strong> Prioritize and plan your team's work on the backlog. Break down work from your project roadmap, and order work items so your team knows what to deliver first."
        />,
        <FormattedMessage
            key="tabs.tab1.item2"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab1.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Organize cycles of work into sprints:</strong> Use sprints to drive incremental delivery, allow your team to ship high-quality work and deliver value faster."
        />,
        <FormattedMessage
            key="tabs.tab1.item3"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab1.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Understand your team’s velocity and capacity:</strong> Improve predictability on planning and delivery with out-of-the-box reports, including the sprint report and velocity chart."
        />
    ];
    const imageSrc =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:1ab8aa94-32ad-4166-a4c0-82bbe5ee6b44/scrum_template%201.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab1.imageAlt",
        defaultMessage: "Scrum projects in Jira"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const Kanban = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab2.heading",
        defaultMessage:
            "Maximize efficiency by moving forward the work that matters most."
    });
    const listItems = [
        <FormattedMessage
            key="tabs.tab2.item1"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab2.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Easily track work at a glance:</strong> Work items are represented visually on your kanban board, so teams can quickly check the most up-to-date status of projects."
        />,
        <FormattedMessage
            key="tabs.tab2.item2"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab2.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Limit work in progress to improve efficiency:</strong> Reduce bottlenecks by setting the maximum amount of work that can exist in each status with work in progress limits."
        />,
        <FormattedMessage
            key="tabs.tab2.item3"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab2.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Use data to drive continuous improvement:</strong> Help ensure your teams are consistently delivering maximum value back to your business, with agile reports like the cumulative flow diagram."
        />
    ];
    const imageSrc =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:da502cfe-c7e7-490a-9a81-f049cb54f9f9/JSW_Kanban.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab2.imageAlt",
        defaultMessage: "Kanban board in Jira"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const BugTracker = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab3.heading",
        defaultMessage: "Report, track, and prioritize bugs."
    });
    const listItems = [
        <FormattedMessage
            key="tabs.tab3.item1"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab3.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Identify and capture bugs:</strong>  See all your bugs in one place. Capture its details by creating an customizable issue from anywhere in your project."
        />,
        <FormattedMessage
            key="tabs.tab3.item2"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab3.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Assign and prioritize:</strong> Once captured, squash bugs before they cause trouble. Bugs can be prioritized based on importance, urgency, and your team’s workload capacity."
        />,
        <FormattedMessage
            key="tabs.tab3.item3"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab3.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Optimized for debugging:</strong> Use a template workflow created specifically for bug resolution to keep teams focused."
        />
    ];
    const imageSrc =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:637fda65-d340-4207-9177-1accf2629210/JSW_Bug%20Tracking.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab3.imageAlt",
        defaultMessage: "screenshot of a Jira board"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};

const DevOps = () => {
    const intl = useIntl();

    const title = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab4.heading",
        defaultMessage:
            "Develop, deploy, and manage applications with an open tools approach."
    });
    const listItems = [
        <FormattedMessage
            key="tabs.tab4.item1"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab4.item1"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Connected tools:</strong> With Jira Software as the backbone, Open DevOps connects every tool in your toolchain, so the entire team is up to date on real-time status updates."
        />,
        <FormattedMessage
            key="tabs.tab4.item2"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab4.item2"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Open approach:</strong> Get an open toolchain that feels all-in-one. Easily integrate tools your team loves, like Slack, with Atlassian in a single click."
        />,
        <FormattedMessage
            key="tabs.tab4.item3"
            id="JiraSoftwarePagePaidLpJswTemplate.tabs.tab4.item3"
            values={DEFAULT_VALUES}
            defaultMessage="<strong>Scalable workflows:</strong> Get started with tools, practices, and DevOps automation rules that work out of the box. As you scale, customize your workflow without sacrificing coordination."
        />
    ];
    const imageSrc =
        "https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:94f1784b-606a-4f3e-a478-09a8400982c7/devOps_template%201.png?cdnVersion=ece";
    const imageAlt = intl.formatMessage({
        id: "JiraSoftwarePagePaidLpJswTemplate.tabs.tab4.imageAlt",
        defaultMessage: "code template"
    });

    return (
        <ImageChecklistSide
            title={title}
            listItems={listItems}
            imageSrc={imageSrc}
            imageAlt={imageAlt}
        />
    );
};
